import { IShippingAddress } from '../types/index'
import { repeat, getReturnItemsValue } from '../utils/helpers'
import { DHLCreatePickupsResponse } from '../types/Courier/DHL/DHLCreatePickupsResponse'
import { UPSPlannedPickupDateAndTime } from '../types/Courier/UPS/UPSPlannedPickupDateAndTime'
import { raygun } from '@ynap/clientside-monitoring'

type PackageObject = {
    weight: number,
    dimensions: {
        length: number,
        width: number,
        height: number,
    };
}

const reportToRaygun = (message, { requestBody, error, trackingUrl, returnTrackingUrl }) =>
    raygun.helpers.agent('send', {
        error: new Error(message),
        tags: ['DHL', 'book-a-return', 'create-dhl-pickups-error', requestBody.customerDetails.shipperDetails.countryCode],
        customData: {
            requestBody,
            errorString: error.toString(),
            trackingUrl, 
            returnTrackingUrl
        },
    })

const splitLines = (addressLine: Array<string>, index: number) => {
    const nextIndex = index - 1;
    const lineOne = addressLine[0]
    const addressLineLength = lineOne.split(' ').slice(0, nextIndex).join(' ').length;

    return addressLineLength > 45 ? splitLines(addressLine, nextIndex) : {
        addressLine1: lineOne.split(' ').slice(0, nextIndex).join(' '),
        addressLine2: lineOne.split(' ').slice(nextIndex).join(' ')
    }
}

// The DHL api only accepts 45 characters per address line, and OMS merges address lines into a single
// string. This, and splitLines functions recursively splits address lines longer than 45 characters into 
// multiple lines. 
export const getFormattedAddressLines = (addressLine: Array<string>) => {
    if (addressLine[0].length > 45) {
        const numOfWords = addressLine[0].split(' ').length
        const addressLines = splitLines(addressLine, numOfWords)

        return addressLines
    }

    return {
        addressLine1: addressLine[0],
    }
}

const createShipments = (
    config: any,
    shippingAddress: IShippingAddress,
    plannedPickupDateAndTime: string | UPSPlannedPickupDateAndTime | undefined,
    numberOfBoxes: number,
    specialInstructions: string,
    returnItems: any[],
    externalOrderId: string,
    productCode: string | undefined,
    trackingUrl:string,
    returnTrackingUrl:string
): Promise<DHLCreatePickupsResponse> => {
    const clientId = config.global?.services.commerce.clientId
    const { collectionService } = config.head
    const { dimensions, baseUrl } = collectionService.couriers.DHL
    const { country, city, zipCode, addressLine, email1, phone1, firstName, lastName } = shippingAddress

    const packageObject: PackageObject = {
        weight: dimensions.weight,
        dimensions: {
            length: dimensions.length,
            width: dimensions.width,
            height: dimensions.height,
        },
    }
    const packages: Array<PackageObject> = repeat(packageObject, numberOfBoxes)

    const packagesInfo = packages.map(p => ({
        ...p,
    }))

    const valueOfGoods = getReturnItemsValue(returnItems);

    const currencyOfGoods = returnItems[0].price.currency.label

    const customerAddress = getFormattedAddressLines(addressLine)

    const externalReturnTrackingNumber = returnItems[0].extnReturnTrackingNo

    const body = {
        orderId: externalOrderId,
        plannedPickupDateAndTime: plannedPickupDateAndTime,
        specialInstructions: [
            {
                value: specialInstructions,
                typeCode: 'TBD',
            },
        ],
        customerDetails: {
            shipperDetails: {
                postalAddress: {
                    postalCode: zipCode,
                    cityName: city,
                    countryCode: country,
                    ...customerAddress,
                },
                contactInformation: {
                    email: email1,
                    phone: phone1,
                    companyName: `${firstName} ${lastName}`,
                    fullName: `${firstName} ${lastName}`,
                },
            },
            pickupDetails: {
                postalAddress: {
                    postalCode: zipCode,
                    cityName: city,
                    countryCode: country,
                    ...customerAddress,
                },
                contactInformation: {
                    email: email1,
                    phone: phone1,
                    companyName: `${firstName} ${lastName}`,
                    fullName: `${firstName} ${lastName}`,
                },
            },
        },
        shipmentTrackingNumber: externalReturnTrackingNumber,
        shipmentDetails: [
            {
                productCode: productCode,
                declaredValue: valueOfGoods,
                declaredValueCurrency: currencyOfGoods,
                unitOfMeasurement: dimensions.unitOfMeasurement,
                packages: packagesInfo,
            }
        ],
    }

    const createShipment =
        fetch(`${baseUrl}/pickups`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
                'x-ibm-client-id': clientId,
            },
        }).then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                reportToRaygun(`Unsucessful response when hitting ${baseUrl}/pickups`, { requestBody: body, error, trackingUrl, returnTrackingUrl })
                throw new Error(error)
            }
            return res.json()
        }).catch(e => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/pickups`, { requestBody: body, error: e, trackingUrl, returnTrackingUrl })
            throw new Error(e)
        })

    return createShipment
}

export default createShipments